import { useEffect, useState, useCallback } from "react";
import { Loader2, TrendingUp, ShoppingCart, Users, Receipt, Eye, Tag, Search as SearchIcon, Download, Database } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { catLabel } from "@/lib/taxonomy";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

const RANGES = [{ d: 7, label: "7 días" }, { d: 30, label: "30 días" }, { d: 90, label: "90 días" }];

export const AnalyticsPanel = () => {
  const { settings } = useSettings();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/analytics?days=${days}`);
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const [downloading, setDownloading] = useState("");
  const download = async (entity, filename) => {
    setDownloading(entity);
    try {
      const res = await api.get(`/admin/export/${entity}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Respaldo descargado");
    } catch {
      toast.error("No se pudo generar el respaldo");
    } finally {
      setDownloading("");
    }
  };

  const cur = settings.currency || "₡";
  const maxDay = data ? Math.max(1, ...data.orders_per_day.map((d) => d.revenue)) : 1;

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        {RANGES.map((r) => (
          <button
            key={r.d}
            data-testid={`analytics-range-${r.d}`}
            onClick={() => setDays(r.d)}
            className={`px-4 py-2 rounded-2xl text-sm font-semibold border transition-all ${days === r.d ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {loading || !data ? (
        <div className="grid place-items-center py-20"><Loader2 className="w-7 h-7 animate-spin text-coral" /></div>
      ) : (
        <div className="space-y-6" data-testid="analytics-content">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Kpi icon={TrendingUp} label="Ingresos estimados" value={formatPrice(data.revenue, cur)} testid="kpi-revenue" />
            <Kpi icon={Receipt} label="Pedidos" value={data.order_count} testid="kpi-orders" />
            <Kpi icon={Tag} label="Ticket promedio" value={formatPrice(data.avg_ticket, cur)} testid="kpi-avg-ticket" />
            <Kpi icon={Users} label="Visitas únicas" value={data.unique_visitors} testid="kpi-visitors" />
            <Kpi icon={ShoppingCart} label="Agregados al carrito" value={data.add_to_cart} testid="kpi-add-to-cart" />
            <Kpi icon={Eye} label="Vistas de producto" value={data.product_views} testid="kpi-product-views" />
          </div>

          <Card title="Pedidos e ingresos por día">
            {data.orders_per_day.length === 0 ? (
              <Empty text="Aún no hay pedidos en este periodo." />
            ) : (
              <div className="flex items-end gap-1.5 h-40 overflow-x-auto pt-2">
                {data.orders_per_day.map((d) => (
                  <div key={d.date} className="flex flex-col items-center gap-1 min-w-[28px]" title={`${d.date}: ${formatPrice(d.revenue, cur)} · ${d.orders} pedido(s)`}>
                    <div className="w-6 rounded-t-lg bg-coral/80 hover:bg-coral transition-colors" style={{ height: `${Math.max(6, (d.revenue / maxDay) * 130)}px` }} />
                    <span className="text-[9px] text-muted-moss rotate-0">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <RankCard title="Categorías más navegadas" icon={Tag} items={data.top_categories} render={(l) => catLabel(l)} testid="top-categories" />
            <RankCard title="Productos más vistos" icon={Eye} items={data.top_products} testid="top-products" />
            <RankCard title="Búsquedas frecuentes" icon={SearchIcon} items={data.top_searches} testid="top-searches" />
          </div>

          <div data-testid="backup-section" className="bg-white rounded-2xl border border-[#E3E7E2] p-4">
            <div className="flex items-center gap-2 text-charcoal mb-1">
              <Database className="w-4 h-4 text-coral" />
              <p className="text-sm font-bold">Respaldo de datos</p>
            </div>
            <p className="text-xs text-muted-moss mb-3">Descarga una copia de tu información. El respaldo completo incluye pedidos, clientes, miembros, productos y analítica en un ZIP.</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="backup-all" onClick={() => download("all", `respaldo-${(settings.store_name||'tienda').toLowerCase().replace(/\s+/g,'-')}.zip`)} disabled={!!downloading} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-coral text-white font-bold hover:bg-coral-dark active:scale-95 transition-all disabled:opacity-60">
                {downloading === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Respaldo completo (ZIP)
              </button>
              <BackupBtn t="orders" label="Pedidos (CSV)" file="pedidos.csv" downloading={downloading} onClick={download} />
              <BackupBtn t="customers" label="Clientes (CSV)" file="clientes.csv" downloading={downloading} onClick={download} />
              <BackupBtn t="members" label="Miembros (CSV)" file="miembros.csv" downloading={downloading} onClick={download} />
              <BackupBtn t="products" label="Productos (CSV)" file="productos.csv" downloading={downloading} onClick={download} />
              <BackupBtn t="analytics" label="Analítica (JSON)" file="analitica.json" downloading={downloading} onClick={download} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BackupBtn = ({ t, label, file, downloading, onClick }) => (
  <button data-testid={`backup-${t}`} onClick={() => onClick(t, file)} disabled={!!downloading} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[#E3E7E2] text-charcoal font-semibold hover:bg-sage/40 active:scale-95 transition-all disabled:opacity-60">
    {downloading === t ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {label}
  </button>
);

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#E3E7E2] p-4">
    <div className="flex items-center gap-2 text-muted-moss">
      <Icon className="w-4 h-4" />
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
    </div>
    <p className="font-display text-2xl font-bold text-charcoal mt-1.5">{value}</p>
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-[#E3E7E2] p-4">
    <p className="text-sm font-bold text-charcoal mb-3">{title}</p>
    {children}
  </div>
);

const RankCard = ({ title, icon: Icon, items, render, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#E3E7E2] p-4">
    <div className="flex items-center gap-2 text-charcoal mb-3">
      <Icon className="w-4 h-4 text-coral" />
      <p className="text-sm font-bold">{title}</p>
    </div>
    {(!items || items.length === 0) ? (
      <Empty text="Sin datos aún." />
    ) : (
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-2 text-sm">
            <span className="text-charcoal line-clamp-1">{render ? render(it.label) : it.label}</span>
            <span className="shrink-0 font-bold text-coral bg-peach/50 rounded-full px-2 py-0.5 text-xs">{it.count}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Empty = ({ text }) => <p className="text-sm text-muted-moss py-4 text-center">{text}</p>;
